/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	OnInit
} from '@angular/core';
import {
	CommissionsHelper
} from '@insurance/helpers/commissions..helper';
import {
	CommonDashboardComponent
} from '@shared/components/common-dashboard/common-dashboard.component';
import {
	CommonTableComponent
} from '@shared/components/common-table/common-table.component';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	CommonTablePageDirective
} from '@shared/directives/common-table-page.directive';
import {
	ICommonTableColumn
} from '@shared/interfaces/application-objects/common-table-column.interface';
import {
	ICommonTable
} from '@shared/interfaces/application-objects/common-table.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ResolverService
} from '@shared/services/resolver.service';
import {
	cloneDeep
} from 'lodash-es';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	TableHelper
} from '@shared/helpers/table.helper';
import {
	CommissionDisbursementRetryExpandComponent
} from './commission-disbursement-retry-expand/commission-disbursement-retry-expand.component';
import {
	DocumentHelper
} from '@shared/helpers/document.helper';
import {
	EntityInstanceComponent
} from '@entity/components/entity-instance/entity-instance.component';
import {
	StringHelper
} from '@shared/helpers/string.helper';

/* eslint-enable max-len */

@Component({
	selector: 'commission-disbursement-summary',
	templateUrl: './commission-disbursement-summary.component.html'
})

/**
 * A component representing a commissions disbursment list component.
 *
 * @export
 * @class CommissionDisbursementSummaryComponent
 * @extends CommonTablePageDirective
 * @implements {IDynamicComponent<Component, any>}
 * @implements {OnInit}
 */
export class CommissionDisbursementSummaryComponent
	extends CommonTablePageDirective
	implements OnInit, IDynamicComponent<CommonDashboardComponent, any>
{
	/**
	 * Initializes an instance of the commission disbursement list component.
	 *
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The api service used to load entity instance data.
	 * @param {ActivityService} activityService
	 * @param {ResolverService} resolver
	 * The resolver service used in this component.
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public constructor(
		public entityInstanceApiService: EntityInstanceApiService,
		public activityService: ActivityService,
		public resolver: ResolverService)
	{
		super(resolver);
	}

	/**
	 * Gets or sets the context that will be set when implementing this
	 * as a dynamic component.
	 *
	 * @type {IDynamicComponentContext<CommonDashboardComponent, any>}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public context: IDynamicComponentContext<CommonDashboardComponent, any>;

	/**
	 * Gets or sets the entity instance component associated to this drawer
	 * component.
	 *
	 * @type {EntityInstanceComponent}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public entityInstanceComponent: EntityInstanceComponent;

	/**
	 * Gets or sets the common table.
	 *
	 * @type {ICommonTable}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public commonTable: ICommonTable;

	/**
	 * Gets or sets the selected table filter.
	 *
	 * @type {string}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public tableFilter: string = 'All';

	/**
	 * Gets or sets the list of commission disbursements.
	 *
	 * @type {any[]}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public commissionDisbursementEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the list of commission disbursements.
	 *
	 * @type {any[]}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public disbursements: any[] = [];

	/**
	 * Gets or sets the list of filtered disbursements.
	 *
	 * @type {any[]}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public filteredDisbursements: any[] = [];

	/**
	 * Gets or sets the selected row count..
	 *
	 * @type {number}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public selectedRowCount: number = 5;

	/**
	 * Gets the workflow action being executed to retry the commission
	 * disbursement payment.
	 *
	 * @type {string}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public readonly workflowActionName: string =
		'RetryPayments';

	/**
	 * Gets the set of quick filters for this component.
	 *
	 * @type {object}
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public readonly quickfilters:
		{
			all: string;
			positive: string;
			negative: string;
			zero: string;
			ach: string;
			check: string;
		} = {
			all: 'All',
			positive: 'Positive',
			negative: 'Negative',
			zero: 'Zero',
			ach: 'ACH',
			check: 'Check'
		};

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public ngOnInit()
	{
		this.entityInstanceComponent =
			 <EntityInstanceComponent>this.context.source.entityContext.source;

		this.commissionDisbursementEntityInstance =
			this.context.source.entityContext.data.entityInstance;
		this.context.data =
			this.commissionDisbursementEntityInstance;

		this.disbursements =
			cloneDeep(this.commissionDisbursementEntityInstance
				.data
				.disbursements);

		this.setCommissionDisbursementCalculatedValues();
		this.setupTableDefinitions();
		this.setfilteredDisbursements();

		this.loadingTableDefinitions = false;
	}

	/**
	 * Sets the commission disbursement calculated values for commissions and
	 * adjustments amounts.
	 *
	 * @async
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public setCommissionDisbursementCalculatedValues(): void
	{
		this.disbursements.forEach(
			(disbursement: any) =>
			{
				const totalCommissions = CommissionsHelper
					.getCommissionDisbursementCommissionsByAgencyId(
						this.commissionDisbursementEntityInstance,
						disbursement.organizationId);

				const totalAdjustments = CommissionsHelper
					.getCommissionDisbursementAdjustmentsByAgencyId(
						this.commissionDisbursementEntityInstance,
						disbursement.organizationId);

				disbursement.paymentStatus = StringHelper
					.beforeCapitalSpaces(disbursement.paymentStatus);
				disbursement.commissionsAmount = totalCommissions;
				disbursement.adjustmentsAmount = totalAdjustments;
			});
	}

	/**
	 * Sets up the table definitions for a standard table
	 *
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	public setupTableDefinitions(): void
	{
		this.availableColumns =
			<ICommonTableColumn[]>
			[
				{
					dataKey: 'organizationName.displayName',
					columnHeader: 'Agency',
					displayOrder: 1
				},
				{
					dataKey: 'organizationDisbursementMethod',
					columnHeader: 'Disbursement Method',
					displayOrder: 2
				},
				{
					dataKey: 'paymentStatus',
					columnHeader: 'Payment Status',
					displayOrder: 3
				},
				{
					dataKey: 'account',
					columnHeader: 'Account',
					displayOrder: 4
				},
				{
					dataKey: 'commissionsAmount',
					columnHeader: 'Commissions',
					dataFormatType: 'Currency',
					displayOrder: 5
				},
				{
					dataKey: 'adjustmentsAmount',
					columnHeader: 'Adjustments',
					dataFormatType: 'Currency',
					displayOrder: 6
				},
				{
					dataKey: 'total',
					columnHeader: 'Amount',
					dataFormatType: 'Currency',
					displayOrder: 7
				}
			];
		this.selectedColumns =
			[
				...this.availableColumns
			];

		this.commonTable =
			{
				tableTitle: 'Agencies',
				expandTitle: () =>
					TableHelper.getExpandTitle(
						this.commonTableContext,
						'Agency'),
				dataKey: 'organizationId',
				exportable: true,
				tableHeight: {
					virtualPageSizeBased: true,
					minimumDrawerItemHeight: 9
				},
				actions: {
					update: {
						component: CommissionDisbursementRetryExpandComponent,
						layout: [
							{
								key: 'organizationDisbursementMethod',
								type: FormlyConstants
									.customControls
									.customSelect,
								wrappers: [
									FormlyConstants
										.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Payment Method Type',
									appendTo: FormlyConstants
										.appendToTargets
										.body,
									required: true,
									default: 'Check',
									options:
									[
										{
											label: 'ACH',
											value: 'ACH'
										},
										{
											label: 'Check',
											value: 'Check'
										}
									]
								}
							},
							{
								key: 'paymentMessage',
								type: FormlyConstants
									.customControls
									.customTextArea,
								wrappers: [
									FormlyConstants
										.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Payment Result Message',
									rows: FormlyConstants
										.textAreaRowSizes
										.small,
									disabled: true
								}
							}
						],
						items: [
							{
								label: 'Retry Payment',
								styleClass: AppConstants
									.cssClasses
									.pButtonPrimary,
								command: () => this.retryPayment()
							}
						]
					},
					filter: {
						quickFilters:
							[
								{
									label: this.quickfilters.all,
									value: this.quickfilters.all
								},
								{
									label: this.quickfilters.positive,
									value: this.quickfilters.positive
								},
								{
									label: this.quickfilters.negative,
									value: this.quickfilters.negative
								},
								{
									label: this.quickfilters.zero,
									value: this.quickfilters.zero
								},
								{
									label: this.quickfilters.ach,
									value: this.quickfilters.ach
								},
								{
									label: this.quickfilters.check,
									value: this.quickfilters.check
								}
							],
						selectedFilterValue: this.tableFilter
					}
				},
				objectSearch: {
					filter: this.tableFilter,
					orderBy: `Id ${AppConstants.sortDirections.descending}`,
					offset: 0,
					limit: AppConstants.dataLimits.large,
					virtualIndex: 0,
					virtualPageSize: this.tableRowCount
				},
				apiPromise:
					() =>
					{
						this.setfilteredDisbursements();

						return Promise.resolve(this.filteredDisbursements);
					},
				availableColumns: this.availableColumns,
				selectedColumns: this.selectedColumns,
				commonTableContext:
					(commonTableContext:
					IDynamicComponentContext<CommonTableComponent, any>) =>
					{
						commonTableContext.data.entityInstance =
							this.commissionDisbursementEntityInstance;
						commonTableContext.data.commonTable =
							this.commonTable;

						this.commonTableContext =
							commonTableContext;
					}
			};
	}

	/**
	 * Retries the payment process for the selected commission disbursement.
	 *
	 * @async
	 * @memberof CommissionDisbursementRetryExpandComponent
	 */
	public async retryPayment(): Promise<void>
	{
		const selectedItem: any =
			this.commonTableContext.source.selectedItem;

		const id: string =
			selectedItem.resourceIdentifier;
		const disbursementMethod: string =
			selectedItem.organizationDisbursementMethod;
		const agencyName: string =
			selectedItem.organizationName.displayName;

		const queryString: string =
			`?disbursementItemId=${id}`
				+ `&disbursementMethod=${disbursementMethod}`;

		const retryEntityInstance: Function =
			async() =>
			{
				await this.commonTableContext.source.updateSelectedItem();

				DocumentHelper.setElementDisplay(
					AppConstants.cssClasses.entityLayoutMask,
					true);

				this.entityInstanceApiService.entityInstanceTypeGroup =
					AppConstants.typeGroups.commissionDisbursements;

				await this.entityInstanceApiService
					.executeAction(
						this.commissionDisbursementEntityInstance.id,
						this.workflowActionName,
						null,
						queryString);

				await this.entityInstanceComponent.reloadEntity();
			};

		await this.activityService.handleActivity(
			new Activity(
				retryEntityInstance(),
				'<strong>Retrying payment for the selected Agency.</strong>',
				'<strong>Retry Payment Done.</strong>',
				`Payment for ${agencyName} was retried.`,
				`Payment for ${agencyName} was not retried.`));
	}

	/**
	 * Sets the filtered commission disbursements.
	 *
	 * @memberof CommissionDisbursementSummaryComponent
	 */
	private setfilteredDisbursements(): void
	{
		switch (this.commonTable.objectSearch.filter)
		{
			case this.quickfilters.positive:
				this.filteredDisbursements =
					this.disbursements.filter(
						disbursement =>
							disbursement.total > 0);
				break;
			case this.quickfilters.negative:
				this.filteredDisbursements =
					this.disbursements.filter(
						disbursement =>
							 disbursement.total < 0);
				break;
			case this.quickfilters.zero:
				this.filteredDisbursements =
					this.disbursements.filter(
						disbursement =>
							disbursement.total === 0);
				break;
			case this.quickfilters.ach:
				this.filteredDisbursements =
					this.disbursements.filter(
						disbursement =>
							disbursement
								.organizationDisbursementMethod === 'ACH');
				break;
			case this.quickfilters.check:
				this.filteredDisbursements =
					this.disbursements.filter(
						disbursement =>
							disbursement
								.organizationDisbursementMethod === 'Check');
				break;
			default:
				this.filteredDisbursements =
					this.disbursements;
		}
	}
}